/**
 * Admin 非全球玩家 — users not in PHP network-root binary tree; hang-back via placement-move.
 */
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Input, Space, Table, Tag, Typography, message } from 'antd';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';
import { MigrateUserModal } from '../../components/MigrateUserModal';

type OutRow = {
  user_id: number;
  username: string;
  nickname: string | null;
  player_number: string | null;
  status: string;
  parent_id: number | null;
  parent_number: string | null;
  parent_username: string | null;
  position: 'L' | 'R' | null;
  referrer_id: number | null;
  referrer_number: string | null;
  referrer_username: string | null;
  reason: 'null_parent' | 'outside_root' | 'php_only_no_node';
};

type OutResult = {
  network_root_id: number;
  network_root: { id: number; username: string; player_number: string | null };
  total: number;
  has_more: boolean;
  page: number;
  limit: number;
  rows: OutRow[];
};

type ActionTarget = { userId: number; username: string };

const PAGE_SIZE = 50;

export function AdminOutOfNetworkPlayersTable() {
  const qc = useQueryClient();
  const hasNode = useAuthStore((s) => s.hasNode);
  const canHang = hasNode('platform.user.shift_upline');

  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [hangTarget, setHangTarget] = useState<ActionTarget | null>(null);

  useEffect(() => {
    setPage(1);
  }, [q]);

  const tableQuery = useQuery({
    queryKey: ['admin-binary-tree-out-of-network', q, page],
    queryFn: () =>
      api.get<OutResult>('/api/admin/binary-tree/out-of-network', {
        ...(q ? { q } : {}),
        page,
        limit: PAGE_SIZE,
      }),
    staleTime: 60_000,
    retry: 1,
    placeholderData: (prev) => prev,
  });

  function runSearch() {
    setQ(search.trim());
  }

  function refreshTable() {
    void qc.invalidateQueries({
      queryKey: ['admin-binary-tree-out-of-network'],
      refetchType: 'active',
    });
    void qc.invalidateQueries({
      queryKey: ['admin-binary-tree-global-table'],
      refetchType: 'active',
    });
  }

  const data = tableQuery.data;
  const rootLabel =
    data?.network_root?.player_number ??
    data?.network_root?.username ??
    (data?.network_root_id != null ? `#${data.network_root_id}` : '—');

  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }} align="center">
        <Typography.Text type="secondary">
          不在网体内的账户（可挂回）；网体内以系谱全球玩家为准。默认不含虚拟账号；含无 Node 行的 php 镜像账号。
          根 {rootLabel}。
        </Typography.Text>
        <Input
          placeholder="玩家号 / 用户名 / 账号"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onPressEnter={runSearch}
          style={{ width: 220 }}
          allowClear
        />
        <Button type="primary" onClick={runSearch}>
          搜索
        </Button>
        <Button
          onClick={() => {
            setSearch('');
            setQ('');
            setPage(1);
            refreshTable();
          }}
        >
          刷新
        </Button>
        {tableQuery.isError ? (
          <Typography.Text type="danger">
            {tableQuery.error instanceof ApiError
              ? tableQuery.error.message
              : '加载失败'}
          </Typography.Text>
        ) : null}
      </Space>

      <Table<OutRow>
        rowKey={(r) => r.user_id}
        loading={tableQuery.isFetching}
        dataSource={data?.rows ?? []}
        size="small"
        scroll={{ x: 1100 }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: data?.total ?? 0,
          showSizeChanger: false,
          showTotal: (t) => `共 ${t} 人`,
          onChange: (p) => setPage(p),
        }}
        columns={[
          {
            title: '玩家号',
            dataIndex: 'player_number',
            width: 100,
            render: (v: string | null, r) => v ?? r.user_id,
          },
          {
            title: '用户名',
            dataIndex: 'username',
            width: 160,
            ellipsis: true,
          },
          {
            title: '昵称',
            dataIndex: 'nickname',
            width: 120,
            ellipsis: true,
            render: (v: string | null) => v ?? '—',
          },
          {
            title: '状态',
            dataIndex: 'status',
            width: 80,
            render: (v: string) =>
              v === 'active' ? (
                <Tag color="green">active</Tag>
              ) : (
                <Tag>{v}</Tag>
              ),
          },
          {
            title: '原因',
            dataIndex: 'reason',
            width: 100,
            render: (v: OutRow['reason']) =>
              v === 'php_only_no_node' ? (
                <Tag color="purple">仅镜像</Tag>
              ) : v === 'null_parent' ? (
                <Tag color="orange">空挂</Tag>
              ) : (
                <Tag color="red">岛/环</Tag>
              ),
          },
          {
            title: '上级推荐人',
            key: 'parent',
            width: 160,
            render: (_: unknown, r: OutRow) => {
              if (r.parent_id == null) return '—';
              const label = r.parent_number ?? r.parent_username ?? `#${r.parent_id}`;
              return `${label}${r.position ? ` / ${r.position}` : ''}`;
            },
          },
          {
            title: '节点推荐人',
            key: 'ref',
            width: 140,
            ellipsis: true,
            render: (_: unknown, r: OutRow) =>
              r.referrer_number ?? r.referrer_username ?? (r.referrer_id != null ? `#${r.referrer_id}` : '—'),
          },
          {
            title: '操作',
            key: 'actions',
            fixed: 'right',
            width: 100,
            render: (_: unknown, r) =>
              canHang ? (
                <Button
                  type="link"
                  size="small"
                  onClick={() => setHangTarget({ userId: r.user_id, username: r.username })}
                >
                  挂回
                </Button>
              ) : (
                <Typography.Text type="secondary">无权限</Typography.Text>
              ),
          },
        ]}
      />

      {hangTarget ? (
        <MigrateUserModal
          open
          userId={hangTarget.userId}
          username={hangTarget.username}
          placementOnly
          forceSubtreeChoice
          onClose={() => setHangTarget(null)}
          onDone={() => {
            message.success('挂回完成');
            setHangTarget(null);
            refreshTable();
          }}
        />
      ) : null}
    </div>
  );
}
